import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { slugify } from 'transliteration';

import { UploadApplication, useUploadRequestMutation } from '@/graphql/generated';

export type UploadedFile = {
  id?: number | null;
  authToken?: string | null;
  url?: string | null;
  fileName: string;
  fileType: string;
  fileSize: number;
  progress: number;
  onCancel: (msg?: string) => void;
};

type Props = {
  onUploadStatusUpdate: (isUploading: boolean) => void;
};

export type UseUploadResult = {
  handleDeleteUpload: (upload: UploadedFile) => Promise<void>;
  handleUploadFile: (file: File) => Promise<void>;
  uploads: UploadedFile[];
};

const UPLOAD_URI = '/upload/file';

export const useUpload = ({ onUploadStatusUpdate }: Props): UseUploadResult => {
  const [uploads, setUploads] = useState<UploadedFile[]>([]);
  const [uploadRequest] = useUploadRequestMutation();
  const isMountedRef = useRef<boolean>(false);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleDeleteUpload = async ({ id, authToken, progress, onCancel }: UploadedFile): Promise<void> => {
    if (progress !== 100) onCancel();

    await axios.delete(`${UPLOAD_URI}/${id}`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Tus-Resumable': '1.0.0',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    return setUploads(uploads.filter((upload) => id !== upload.id));
  };

  const handleUploadFile = async (file): Promise<void> => {
    const { data } = await uploadRequest({
      variables: {
        input: { fileName: file.name, fileSize: file.size, fileType: file.type, application: UploadApplication.Jobs },
      },
    });

    if (data?.uploadRequest?.success) {
      const { id, authToken, url } = data.uploadRequest.success;
      const abortController = new AbortController();
      const onCancel = (): void => {
        abortController.abort();
        onUploadStatusUpdate(uploads.some(({ progress }) => progress !== 100));
      };

      setUploads((prevUploads) => [
        ...prevUploads,
        {
          id,
          authToken,
          url,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          onCancel,
          progress: 0,
        },
      ]);

      onUploadStatusUpdate(true);

      try {
        const response = await axios.patch(`${UPLOAD_URI}/${id}`, file, {
          signal: abortController.signal,
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Upload-Offset': '0',
            'Tus-Resumable': '1.0.0',
            'Content-Type': 'application/offset+octet-stream',
            'Content-Disposition': `attachment; filename="${slugify(file.name)}"`,
            'Upload-Metadata': `content-type="${slugify(file.type)}"`,
          },
          onUploadProgress: ({ loaded, total }) => {
            if (total !== undefined) {
              const progress = Math.floor((loaded / total) * 100);

              setUploads((prevUploads) =>
                prevUploads.map((upload) => (upload.id === id ? { ...upload, progress } : upload))
              );

              onUploadStatusUpdate(progress !== 100);
            }
          },
        });

        if (response.status === 204) {
          onUploadStatusUpdate(uploads.some(({ progress }) => progress !== 100));
          isMountedRef.current &&
            setUploads((prevUploads) =>
              prevUploads.map((upload) => (upload.id === id ? { ...upload, progress: 100 } : upload))
            );
          return;
        }
      } catch (error) {
        setUploads(uploads.filter((upload) => id !== upload.id));
        throw error;
      }
    }

    throw new Error('Error uploading file');
  };

  return { handleUploadFile, handleDeleteUpload, uploads };
};
